import * as React from "react";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";

import { Form, Button, Grid, Divider } from "semantic-ui-react";

import { FormBaseComponent, SaveDispatchProps } from "@neworbit/simpleui-forms";

import { FileUpload } from "@teamprevent/common/fileUploads";

import { acceptableExtensions } from "@teamprevent/common/acceptableExtensions";

import { createImmunisation } from "../actions";
import { ImmunisationAndEvidenceCreateModel, AppState } from "../model";

import { CreateModal } from "./CreateModal";
import { ImmunisationFields } from "./ImmunisationFields";

import { push } from "redux-little-router";

export interface StateProps {
    open?: boolean;
    employeeRecordId: string;
    basePath?: string;
    onImmunisationsAdd?: (immunisation: ImmunisationAndEvidenceCreateModel) => Promise<void>;
    forceOpen?: boolean;
    preventRedirect?: boolean;
    customerId: string;
}

export interface DispatchProps extends SaveDispatchProps<ImmunisationAndEvidenceCreateModel> {
    close: () => void;
}

export class Create extends FormBaseComponent<ImmunisationAndEvidenceCreateModel, StateProps> {
    constructor(props: StateProps & SaveDispatchProps<ImmunisationAndEvidenceCreateModel>) {
        super(props);
        this.state = {
            values: {
                notes: "",
                immunisationTypeName: null,
                dateCompleted: null,
                evidence: [],
                recordType: null,
                outcome: null,
                identityValidation: null,
                completedByName: null
            },
            valid: {}
        };
        this.onImmunisationUpdated = this.onImmunisationUpdated.bind(this);
        this.updateProperty = this.updateProperty.bind(this);
    }

    public render() {
        return (
            <Form>
                <ImmunisationFields immunisation={this.state.values} updateProperty={this.updateProperty} showErrors={this.state.showErrors} />
                <Form.Field>
                    <label>Evidence</label>
                    {this.state.values.evidence && this.state.values.evidence.map((evidence, index) =>
                        (<React.Fragment key={index}>
                            <Grid columns="2">
                                <Grid.Column>
                                    {evidence.blobId
                                        ? <a
                                            key={index}
                                            // eslint-disable-next-line max-len
                                            href={`/api/employeeRecord/${this.props.employeeRecordId}/immunisations/evidence/${evidence.blobId}?fileName=${evidence.fileName}`}
                                        >
                                            <Button content={evidence.fileName} type="button" />
                                        </a>
                                        : <FileUpload
                                            apiUrl={`/api/employeeRecord/${this.props.employeeRecordId}/immunisations/evidence`}
                                            // eslint-disable-next-line react/jsx-no-bind
                                            onFileChange={(blobId, fileName) => this.changeEvidence(index, blobId, fileName)}
                                            fileName={evidence.fileName}
                                            acceptExtensions={acceptableExtensions}
                                        />}
                                </Grid.Column>
                                <Grid.Column textAlign="right" verticalAlign="middle">
                                    <Button
                                        className="remove-evidence-button"
                                        content="Remove"
                                        type="button"
                                        // eslint-disable-next-line react/jsx-no-bind
                                        onClick={() => this.removeEvidence(index)}
                                        floated="right"
                                    />
                                </Grid.Column>
                            </Grid>
                            <Divider />
                        </React.Fragment>))
                    }
                    <Form.Button type="button" content="Add Evidence" onClick={this.addEvidence} />
                </Form.Field>
            </Form>
        );
    }

    public submit(): void {
        this.handleSubmit({ preventDefault: (): void => undefined } as any);
    }

    public changeEvidence(index: number, blobId: string, fileName: string): void {
        this.setState({
            values: {
                ...this.state.values,
                evidence: [
                    ...this.state.values.evidence.slice(0, index),
                    { blobId, fileName },
                    ...this.state.values.evidence.slice(index + 1)
                ]
            }
        });
    }

    public removeEvidence(index: number): void {
        this.setState({
            values: {
                ...this.state.values,
                evidence: [
                    ...this.state.values.evidence.slice(0, index),
                    ...this.state.values.evidence.slice(index + 1)
                ]
            }
        });
    }

    public addEvidence = (): void => {
        this.setState({
            values: {
                ...this.state.values,
                evidence: [
                    ...this.state.values.evidence,
                    { blobId: null, fileName: null }
                ]
            }
        });
    };

    public onImmunisationUpdated = (updatedImmunisation: ImmunisationAndEvidenceCreateModel) => {
        this.setState(prevState => ({
            ...prevState,
            values: updatedImmunisation
        }));
    };
}

function mapStateToProps(state: AppState): StateProps {
    return {
        open: state.router.pathname.endsWith("/create"),
        basePath: state.router.pathname.substring(0, state.router.pathname.indexOf("immunisations") + "immunisations".length),
        employeeRecordId: state.router.params.employeeRecordId,
        customerId: state.router.params.customerId
    };
}

function mapDispatchToProps(dispatch: ThunkDispatch<AppState, undefined, any>) {
    return {
        dispatchSave: (employeeRecordId: string, customerId: string, immunisation: ImmunisationAndEvidenceCreateModel, basePath: string) =>
            dispatch(createImmunisation(employeeRecordId, customerId, immunisation, basePath)),
        dispatchClose: (basePath: string) => dispatch(push(basePath))
    };
}

function mergeProps(propsFromState: StateProps, propsFromDispatch: ReturnType<typeof mapDispatchToProps>): StateProps & DispatchProps {
    return {
        ...propsFromState,
        open: propsFromState.forceOpen || propsFromState.open,
        save: (immunisation: ImmunisationAndEvidenceCreateModel) =>
            propsFromState.onImmunisationsAdd ?
                propsFromState.onImmunisationsAdd({
                    ...immunisation,
                    evidence: immunisation.evidence.filter(e => e.blobId)
                }) :
                propsFromDispatch.dispatchSave(propsFromState.employeeRecordId, propsFromState.customerId, {
                    ...immunisation,
                    evidence: immunisation.evidence.filter(e => e.blobId)
                }, propsFromState.basePath),
        close: () => propsFromDispatch.dispatchClose(propsFromState.basePath)
    };
}

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(CreateModal);
